/// <reference types="node" />
import { Modal } from '@shared/Modal';
import Button from '@shared/Button';
import Card from '@shared/Card';
import { Download, RotateCcw, Trash2, User, Clock, Hash, AlertTriangle } from 'lucide-react';
import FileIcon from './FileIcon';
import StatusBadge from './StatusBadge';
import api from '@/utils/api';
import { useEffect, useState } from 'react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { toast } from 'react-hot-toast';

interface UploadedBy {
  name?: string;
  email?: string;
}

interface RecentQuery {
  query: string;
  timestamp: string;
  status: string;
}

interface Document {
  id: string;
  _id: string;
  originalName: string;
  fileType: string;
  fileSize: number;
  pages?: number;
  uploadedAt: string;
  uploadedBy?: UploadedBy;
  processingTime?: number;
  chunks?: number;
  queryCount?: number;
  lastQueried?: string;
  status: string;
  errorMessage?: string;
  recentQueries?: RecentQuery[];
}

interface DocumentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  onReprocess: (id: string) => void;
  onDelete: (document: Document) => void;
  onDownload: (document: Document) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DocumentDetailsModal: React.FC<DocumentDetailsModalProps> = ({ isOpen, onClose, document: initialDocument, onReprocess, onDelete, onDownload }) => {
  const [document, setDocument] = useState<Document | null>(initialDocument);
  const [loading, setLoading] = useState(true);
  const [processingPollId, setProcessingPollId] = useState<NodeJS.Timeout | null>(null);

  const fetchDocumentDetails = async (docId: string) => {
    setLoading(true);
    try {
      const fetchedDoc = await api.getDocument(docId);
      setDocument(fetchedDoc);

      // If document is processing, start polling
      if (fetchedDoc.status === 'processing') {
        if (!processingPollId) {
          const id = setInterval(() => fetchDocumentDetails(docId), 3000); // Poll every 3 seconds
          setProcessingPollId(id);
        }
      } else if (processingPollId) {
        clearInterval(processingPollId);
        setProcessingPollId(null);
      }

    } catch (error) {
      console.error('Failed to fetch document details:', error);
      toast.error('Failed to load document details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && initialDocument?.id) {
      fetchDocumentDetails(initialDocument.id);
    } else {
      // Clear any active polling when modal closes
      if (processingPollId) {
        clearInterval(processingPollId);
        setProcessingPollId(null);
      }
    }
    return () => {
      if (processingPollId) {
        clearInterval(processingPollId);
      }
    };
  }, [isOpen, initialDocument?.id]);

  if (!document && loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Loading Document...">
        <div className="flex justify-center items-center h-48">
          {/* Assuming Loader2 is imported or available globally, if not, replace with a simple spinner */}
          <svg className="w-8 h-8 animate-spin text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 0020 10a8.001 8.001 0 00-15.356-2m0 0v5h.581m-.004 3h.008c.282.001.564.002.846.002.735 0 1.46-.036 2.164-.108m.956-2.197c.48.067.973.111 1.472.138m.722 0c.264.014.53.024.795.03m-.795 0c-.265-.006-.53-.016-.795-.03M19 19v-5h.582m-15.356 2A8.001 8.001 0 014 14c0-.735.036-1.46.108-2.164M4 12c0 4.418 3.582 8 8 8s8-3.582 8-8-3.582-8-8-8-8 3.582-8 8z"></path>
          </svg>
        </div>
      </Modal>
    );
  }

  if (!document) return null; // Should not happen if loading state is handled

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={document.originalName}>
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <FileIcon fileType={document.fileType} className="w-8 h-8" />
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{document.originalName}</h3>
            <StatusBadge status={document.status} className="mt-1" />
          </div>
        </div>

        <Card>
          <h4 className="font-semibold text-gray-800 mb-3">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Original Name:</p>
              <p className="font-medium">{document.originalName}</p>
            </div>
            <div>
              <p className="text-gray-500">File Type:</p>
              <p className="font-medium">{document.fileType}</p>
            </div>
            <div>
              <p className="text-gray-500">File Size:</p>
              <p className="font-medium">{formatFileSize(document.fileSize)}</p>
            </div>
            <div>
              <p className="text-gray-500">Pages:</p>
              <p className="font-medium">{document.pages || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Uploaded At:</p>
              <p className="font-medium">{format(new Date(document.uploadedAt), 'PPP p')}</p>
            </div>
            <div>
              <p className="text-gray-500">Uploaded By:</p>
              <p className="font-medium flex items-center"><User className="w-4 h-4 mr-1" /> {document.uploadedBy?.name || 'System'} ({document.uploadedBy?.email || 'N/A'})</p>
            </div>
            {document.processingTime && (
              <div>
                <p className="text-gray-500">Processing Time:</p>
                <p className="font-medium">{document.processingTime / 1000} seconds</p>
              </div>
            )}
            {document.chunks && (
              <div>
                <p className="text-gray-500">Number of Chunks:</p>
                <p className="font-medium">{document.chunks}</p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <h4 className="font-semibold text-gray-800 mb-3">Usage Statistics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Total Queries:</p>
              <p className="font-medium text-2xl flex items-center"><Hash className="w-5 h-5 mr-1 text-primary-500" />{document.queryCount || 0}</p>
            </div>
            <div>
              <p className="text-gray-500">Last Queried:</p>
              <p className="font-medium flex items-center"><Clock className="w-4 h-4 mr-1 text-gray-500" />{document.lastQueried ? formatDistanceToNowStrict(new Date(document.lastQueried), { addSuffix: true }) : 'Never'}</p>
            </div>
            {/* Add Average Response Time and Query Success Rate if available in document object */}
          </div>
        </Card>

        {document.status === 'processing' && (
          <Card className="border-yellow-400 bg-yellow-50">
            <h4 className="font-semibold text-yellow-800 mb-2">Processing Details</h4>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 animate-spin text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 0020 10a8.001 8.001 0 00-15.356-2m0 0v5h.581m-.004 3h.008c.282.001.564.002.846.002.735 0 1.46-.036 2.164-.108m.956-2.197c.48.067.973.111 1.472.138m.722 0c.264.014.53.024.795.03m-.795 0c-.265-.006-.53-.016-.795-.03M19 19v-5h.582m-15.356 2A8.001 8.001 0 014 14c0-.735.036-1.46.108-2.164M4 12c0 4.418 3.582 8 8 8s8-3.582 8-8-3.582-8-8-8-8 3.582-8 8z"></path>
              </svg>
              <p className="text-yellow-700">Document is currently being processed. Please wait...</p>
            </div>
          </Card>
        )}

        {document.status === 'failed' && (
          <Card className="border-error bg-red-50">
            <h4 className="font-semibold text-error mb-2">Processing Failed</h4>
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-error" />
              <p className="text-red-700">An error occurred during document processing:</p>
            </div>
            <p className="text-sm text-red-600 bg-red-100 p-3 rounded-md">{document.errorMessage || 'No specific error message provided.'}</p>
            <Button onClick={() => onReprocess(document._id)} variant="primary" className="mt-4">
              <RotateCcw className="w-4 h-4 mr-2" /> Reprocess Document
            </Button>
          </Card>
        )}

        {/* Recent Queries Section (mock/placeholder for Phase 1) */}
        {document.recentQueries && document.recentQueries.length > 0 && (
          <Card>
            <h4 className="font-semibold text-gray-800 mb-3">Recent Queries</h4>
            <ul className="space-y-2 text-sm">
              {document.recentQueries.slice(0, 5).map((query, index) => (
                <li key={index} className="flex justify-between items-center py-2 border-b last:border-b-0">
                  <p className="text-gray-700 truncate"><span className="font-medium">Query:</span> {query.query}</p>
                  <p className="text-gray-500 text-xs ml-4">{formatDistanceToNowStrict(new Date(query.timestamp), { addSuffix: true })} - <StatusBadge status={query.status} /></p>
                </li>
              ))}
            </ul>
            <Button variant="ghost" className="mt-4">View all in Analytics</Button>
          </Card>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <Button onClick={() => onDownload(document)} variant="secondary">
          <Download className="w-4 h-4 mr-2" /> Download Original
        </Button>
        {document.status === 'failed' && (
          <Button onClick={() => onReprocess(document._id)} variant="secondary">
            <RotateCcw className="w-4 h-4 mr-2" /> Reprocess Document
          </Button>
        )}
        <Button onClick={() => onDelete(document)} variant="danger">
          <Trash2 className="w-4 h-4 mr-2" /> Delete Document
        </Button>
        <Button onClick={onClose} variant="ghost">
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default DocumentDetailsModal;
